import { TAuthUser } from '../store/general/types';
import { IUserRestResponse, UserRest } from '../../common/rest/user/userRest';
import { getDispatch, getState } from '../store';
import { getAuthUser, getAuthUserLangCode } from '../store/general/selectors';
import { ContentLibActions } from '../store/contentLib/actions';
import { setAuthUserAction } from '../store/general/actions';
import { AuthManager } from '../../common/utils/auth-manager';
import { resetModelsAction } from '../store/models/actions';
import { EventsRouter } from '../../common/events/eventsRouter';
import { Events } from '../../common/events/types';
import { prepareAfterOpen } from './prepareAfterOpen';
import { LoginUserEffects } from './loginUserEffects';
import { Navigator } from './navigator';
import { EMPTY_ARRAY } from '../../common/types';
import { ContentLibSelectors } from '../store/contentLib/selectors';
import { UrlUtil } from '../../common/utils/url-util';
import { CallSelectors } from '../store/call/selectors';

export type TUserOnLoginCallback = (loginResult: IUserRestResponse, targetLangName: string, nativeLangName: string) => Promise<void>
export type TUserLoginHandler = () => Promise<IUserRestResponse | null>

export class AuthUserEffects {

  public static async updateUser(data: Partial<TAuthUser>) {
    let authUser = getAuthUser(getState());
    if (authUser) {
      await UserRest.updateUser(data);
      authUser = {...authUser, ...data};
      getDispatch()(setAuthUserAction(authUser));
    }
  }

  public static async setLang(langCode: string) {
    if (langCode && getAuthUserLangCode(getState()) === langCode) {
      return Promise.resolve();
    }
    if (langCode) {
      await this.updateUser({langCode});

      const dispatch = getDispatch();

      const { targetLangCode } = ContentLibSelectors.getSearchFilter(getState());
      if (targetLangCode) { // if not set, set at ContentLib
        dispatch(ContentLibActions.setSearchFilter({ targetLangCode: langCode }));
      }
      dispatch(ContentLibActions.setVideosSearchResultExist(false));
      dispatch(ContentLibActions.setChannels(EMPTY_ARRAY));

      return Promise.resolve();
    }
    return Promise.reject(new Error('no langCode to set'));
  }

  public static async sendConfirmEmail() {
    let authUser = getAuthUser(getState());
    if (authUser) {
      const result: Partial<TAuthUser> = await UserRest.sendConfirmEmail();
      authUser = {...authUser, ...result};
      getDispatch()(setAuthUserAction(authUser));
    }
  }

  public static async loginProcess(params: {
    loginHandler?: TUserLoginHandler,
    newUserCallback: TUserOnLoginCallback,
    onLoginUserCallback?: TUserOnLoginCallback,
    targetLangName: string,
    nativeLangName: string,
    disableNavigateOnLogin?: boolean
  }): Promise<IUserRestResponse | null> {
    const {newUserCallback, targetLangName, nativeLangName, disableNavigateOnLogin, onLoginUserCallback, loginHandler} = params
    const dispatch = getDispatch();
    try {
      const _loginHandler: TUserLoginHandler = loginHandler || AuthManager.login;
      const result = await _loginHandler();
      if (!result || !result.authUser)
        return null;
      dispatch(resetModelsAction());
      if (onLoginUserCallback) {
        await onLoginUserCallback(result, targetLangName, nativeLangName);
      }
      if (result.authUser.isNew) {
        EventsRouter.trackEvent(Events.AUTHORIZATION, new Date(), {
          userId: result.authUser.hashId
        });
        EventsRouter.trackEvent(Events.NEW_USER_SIGNED_UP, undefined, {
          email: result.authUser.email,
        });

        await newUserCallback(result, targetLangName, nativeLangName);

      } else {
        await dispatch(prepareAfterOpen(result.authUser, result.settings, result.appSettings, disableNavigateOnLogin));
      }
      return result;
    } catch (e) {
      console.log('err', e);
      return null;
    }
  }

  public static async newUserDefaultHandler(result: IUserRestResponse) {
    await LoginUserEffects.prepareBeforeShowSelectLangs(result);
    if (UrlUtil.getRouteCall()?.roomId || CallSelectors.getRoomId(getState())) {
      // none
    } else {
      Navigator.openInstallSelectLangs();
    }
  }

}
